import { uniqueId } from 'lodash';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';

import { TId } from '@/features/common';
import { useNotifications } from '@/features/notifications';

import { TAutomationTriggerInput, TAutomationTriggerMap } from '../../types';

import { CANDIDATE_QUERY_KEY } from '@/features/candidate/queries';
import { PIPELINE_CANDIDATES_KEY } from '@/features/pipeline/queries';
import { useRunAutomationsMutation } from '@/queries/automations/mutations';
import { useQueryClient } from '@tanstack/react-query';
import {
  TRIGGER_NOTIFICATION_DEFAULTS,
  useBeforeUnloadCheck,
} from './automations-trigger.utils';
import { TriggerNotifications } from './ui';

interface IAutomationsTriggerContext {
  automationTriggers: TAutomationTriggerMap;
  addAutomationTrigger(automationTrigger: TAutomationTriggerInput): void;
  removeAutomationTrigger(id: TId): void;
  runAutomationTrigger(id: TId): void;
  /** Waits for all triggers — used in logout and window.beforeunload event */
  runAllAutomationTriggers(): Promise<PromiseSettledResult<unknown>[]>;
}

const AutomationsTriggerContext = createContext<IAutomationsTriggerContext>({
  automationTriggers: new Map(),
  addAutomationTrigger: () => {},
  removeAutomationTrigger: () => {},
  runAutomationTrigger: () => {},
  runAllAutomationTriggers: () => Promise.resolve([]),
});

export function AutomationsTriggerProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { addNotification } = useNotifications();
  const queryClient = useQueryClient();
  const runAutomationMutation = useRunAutomationsMutation({
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          ...PIPELINE_CANDIDATES_KEY,
          variables.opening_id,
          variables.stage_id,
        ],
      });
      variables.data.candidate_ids.forEach((candidateId) => {
        queryClient.invalidateQueries({
          queryKey: [...CANDIDATE_QUERY_KEY, candidateId],
        });
      });
    },
  });

  const [automationTriggers, setAutomationTriggers] =
    useState<TAutomationTriggerMap>(new Map());
  const ranAutomations = useRef<Set<TId>>(new Set());

  const addAutomationTrigger = useCallback(
    (automationTrigger: TAutomationTriggerInput) => {
      const defaults = TRIGGER_NOTIFICATION_DEFAULTS[automationTrigger.type];
      const mergedContent = {
        ...defaults,
        ...automationTrigger,
      };

      setAutomationTriggers((prev) => {
        const next = new Map(prev);
        next.set(uniqueId('automation-trigger-'), mergedContent);
        return next;
      });
    },
    []
  );

  const removeAutomationTrigger = useCallback(
    (id: TId) => {
      setAutomationTriggers((prev) => {
        const next = new Map(prev);
        next.delete(id);
        return next;
      });

      addNotification({
        type: 'automation_action_success',
        meta: { action: 'cancelled' },
      });
    },
    [addNotification]
  );

  const runAutomationTrigger = useCallback(
    (id: TId) => {
      if (ranAutomations.current.has(id)) return;

      const automationTrigger = automationTriggers.get(id);
      if (!automationTrigger) return;

      // Remove immediately to prevent duplicates
      ranAutomations.current.add(id);
      setAutomationTriggers((prev) => {
        const next = new Map(prev);
        next.delete(id);
        return next;
      });

      runAutomationMutation.mutate(
        {
          workspace_id: Number(automationTrigger.workspaceId),
          opening_id: Number(automationTrigger.jobOpeningId),
          stage_id: Number(automationTrigger.stageId),
          automation_id: Number(automationTrigger.automationId),
          data: { candidate_ids: automationTrigger.candidateIds.map(Number) },
        },
        {
          onSuccess: () => {
            addNotification({
              type: 'automation_run_success',
              meta: {
                message: automationTrigger.successMessage,
              },
            });
          },
        }
      );
    },
    [addNotification, automationTriggers, runAutomationMutation]
  );

  const runAllAutomationTriggers = useCallback(async () => {
    if (automationTriggers.size === 0) return Promise.allSettled([]);

    const triggers = Array.from(automationTriggers.entries());
    const triggerCount = triggers.length;

    // Remove all immediately to prevent duplicates
    triggers.forEach(([id]) => ranAutomations.current.add(id));
    setAutomationTriggers(new Map());

    const promises = triggers.map(([_, trigger]) =>
      runAutomationMutation.mutateAsync({
        workspace_id: Number(trigger.workspaceId),
        opening_id: Number(trigger.jobOpeningId),
        stage_id: Number(trigger.stageId),
        automation_id: Number(trigger.automationId),
        data: { candidate_ids: trigger.candidateIds.map(Number) },
      })
    );

    const results = await Promise.allSettled(promises);
    const successCount = results.filter((r) => r.status === 'fulfilled').length;
    if (successCount > 0) {
      addNotification({
        type: 'automation_run_success',
        meta: {
          message: `Successfully ran ${successCount} out of ${triggerCount} automation${triggerCount !== 1 ? 's' : ''}`,
        },
      });
    }

    return results;
  }, [addNotification, automationTriggers, runAutomationMutation]);

  useBeforeUnloadCheck(automationTriggers.size > 0, runAllAutomationTriggers);

  return (
    <AutomationsTriggerContext.Provider
      value={{
        automationTriggers,
        addAutomationTrigger,
        removeAutomationTrigger,
        runAutomationTrigger,
        runAllAutomationTriggers,
      }}
    >
      {children}
      <TriggerNotifications />
    </AutomationsTriggerContext.Provider>
  );
}

export function useAutomationsTrigger(): IAutomationsTriggerContext {
  const context = useContext(AutomationsTriggerContext);
  if (context === undefined) {
    throw new Error(
      'useAutomationsTrigger must be used within a AutomationsTriggerContext.Provider'
    );
  }
  return context;
}
